import type { EventOrLeg, TimeData } from '../../core/TimeData';
import {
  CoreEventType,
  getEventLocationId,
  isCargoEventTimeData,
  isCoaEvent,
  isLeg,
  isPortCallBerthingEventTimeData,
  isPortCallEvent,
  isSingleEvent,
  isSpotEvent,
  isTCOrderEvent,
  isTimeData,
} from '../../core/TimeData';
import type {
  ScenarioLocation,
  ScenarioOrder,
} from '../../../lib/planningData';
import type { EventTimeline } from '../../core/Timelines';
import type { TimelineCompletedVoyage, VoyageAssortment } from '../types';
import { ItemType } from '../types';
import { groupBySequence } from '../../../lib/util';
import { getEventLocation } from '../../core/Core';
import groupBy from 'lodash/groupBy';
import { formatNumber } from '../../../lib/format';
import type { TimelineItem } from '../react-calendar-timeline';

export function getCompletedTimelineItem(
  shipmentTds: TimeData[],
  orderEventsWithGlobals: EventOrLeg[],
  locations: Map<number, ScenarioLocation>,
  shipmentId: string,
  timeline: EventTimeline,
  orders: ScenarioOrder[],
  isCompressedView: undefined | boolean,
  prevItemLocation: number | undefined,
) {
  const group = timeline.id;

  const allTDs = orderEventsWithGlobals.filter(isTimeData);

  const filteredOrderEventsGlobals = allTDs.filter(isCargoEventTimeData) || [];

  let end_time = -Infinity;
  for (const td of filteredOrderEventsGlobals) {
    end_time = Math.max(end_time, td.end);
  }

  const firstEvent = filteredOrderEventsGlobals[0];

  const currentItemLocation = getEventLocationId(firstEvent?.event || {});

  const start_time =
    prevItemLocation === currentItemLocation
      ? shipmentTds[0]?.start
      : (
          orderEventsWithGlobals.find(isPortCallBerthingEventTimeData) ||
          filteredOrderEventsGlobals.find((td) => !isLeg(td)) ||
          firstEvent
        )?.start;

  const assortments: Omit<VoyageAssortment, 'incomingContractType'>[] = [];
  const portCalls: string[] = [];
  groupBySequence(allTDs, (td) => getEventLocationId(td.event)).forEach(
    (locationTds) => {
      const firstTd = locationTds[0];
      const firstEvent = firstTd.event;
      const port = getEventLocation(firstEvent, locations)?.name;
      const prevPortCall = portCalls[portCalls.length - 1] ?? null;
      const isNewPort = prevPortCall !== port;
      if (isNewPort) {
        portCalls.push(port ?? 'Port name N/A');
      }
      locationTds.forEach((td) => {
        const { event } = td;
        if (
          !isTCOrderEvent(event) &&
          !isSingleEvent(event) &&
          !isSpotEvent(event) &&
          !isCoaEvent(event) &&
          !isPortCallEvent(event)
        ) {
          throw new Error(
            `Bad event type for TimelineCompletedVoyage component: ${event.category}`,
          );
        }
        const relatedOrder = 'order' in event ? event.order : null;
        if (relatedOrder && event.type === CoreEventType.Load) {
          const {
            assortmentByAssortmentId,
            cargoUnitByCargoUnitId,
            cargoAmount,
          } = relatedOrder;
          assortments.push({
            assortmentName:
              assortmentByAssortmentId?.name ?? 'Commodity name N/A',
            amount: cargoAmount ? +cargoAmount * td.amount : 0,
            amountUnit: cargoUnitByCargoUnitId?.name || 'mts',
            cargoDeckOption: null,
          });
        }
      });
    },
  );

  const description = Object.values(
    groupBy(assortments, (a) => a.assortmentName),
  )
    .map((group) => {
      const { assortmentName, amount, amountUnit } = group[0];
      const length = group.length;
      return length > 1
        ? `${length} * ${assortmentName}`
        : `${assortmentName} (${formatNumber(amount)} ${amountUnit})`;
    })
    .join(', ');

  const timelineItem: TimelineItem<TimelineCompletedVoyage> = {
    type: ItemType.COMPLETED,
    id: 'completed-' + shipmentId,
    start_time: start_time,
    end_time: end_time,
    group: group,
    cargo: description,
    portCalls: portCalls,
    orders: orders.filter((o) => o.shipmentId === shipmentId),
    isCompressedView: isCompressedView,
    canChangeGroup: false,
    isCollapsed: true,
    canSelect: false,
    canResize: false,
    isShadow: false,
    canMove: false,
  };

  return timelineItem;
}
